import { useParams } from "react-router-dom";
import { ArrowLeft, Printer } from "lucide-react";
import { useGetSessionTimetableQuery } from "../../api/examsApi";

const niqsLogo = "/niqs-logo.png";

function fmtDate(dateStr: string) {
  const [year, month, day] = dateStr.split("T")[0].split("-").map(Number);
  const d = new Date(year, month - 1, day);
  return {
    day:  d.toLocaleDateString("en-NG", { weekday: "long" }).toUpperCase(),
    date: d.toLocaleDateString("en-NG", { day: "2-digit", month: "2-digit", year: "numeric" }),
  };
}

export default function MemberExamTimetablePage() {
  const { sessionId } = useParams<{ sessionId: string }>();
  const { data, isLoading, isError } = useGetSessionTimetableQuery(Number(sessionId));

  const session     = data?.session;
  const examColumns = Array.isArray(data?.exams)   ? data.exams   : [];
  const rows        = (data?.rows && !Array.isArray(data.rows) && typeof data.rows === "object")
    ? data.rows as Record<string, Record<string, any>>
    : {};
  const sortedDates = Object.keys(rows).sort();

  return (
    <>
      <style>{`
        @media print {
          .no-print { display: none !important; }
          body { margin: 0; }
          #timetable-print { width: 210mm; }
        }
      `}</style>

      <div className="p-6 space-y-6 bg-[#F8FAFC] min-h-screen">
        {/* Top bar */}
        <div className="no-print flex items-center justify-between">
          <div className="flex items-center gap-3">
            <button onClick={() => window.history.back()} className="p-2 rounded-xl hover:bg-slate-200 transition-all">
              <ArrowLeft className="w-4 h-4 text-slate-600" />
            </button>
            <div>
              <h1 className="text-lg font-black text-slate-900 uppercase tracking-widest">Exam Timetable</h1>
              <p className="text-xs text-slate-400 font-bold uppercase tracking-widest">
                {session?.title ?? "Professional Examinations"}
              </p>
            </div>
          </div>
          <button
            onClick={() => window.print()}
            disabled={isLoading || isError}
            className="no-print flex items-center gap-2 px-5 py-2.5 bg-slate-900 text-white text-[10px] font-black uppercase tracking-widest rounded-xl hover:bg-orange-600 transition-all disabled:opacity-50"
          >
            <Printer className="w-4 h-4" /> Print Timetable
          </button>
        </div>

        {isLoading && (
          <div className="flex justify-center py-20">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-orange-500" />
          </div>
        )}

        {isError && (
          <div className="bg-rose-50 border border-rose-200 rounded-2xl p-8 text-center">
            <p className="text-rose-700 font-black text-sm uppercase tracking-widest">Could not load timetable.</p>
            <p className="text-rose-400 text-xs mt-1">The timetable may not have been published yet.</p>
          </div>
        )}

        {data && (
          <div className="flex justify-center">
            <div
              id="timetable-print"
              className="bg-white border-2 border-slate-900 font-sans shadow-2xl"
              style={{ width: "210mm", padding: "10mm", boxSizing: "border-box" }}
            >
              {/* Header */}
              <div className="flex items-center gap-4 border-b-2 border-slate-900 pb-3 mb-4">
                <img src={niqsLogo} alt="NIQS" className="h-16 w-16 object-contain flex-shrink-0"
                  onError={e => { (e.target as HTMLImageElement).style.display = "none"; }} />
                <div className="flex-1 text-center">
                  <h1 className="text-xl font-black tracking-wide leading-tight">
                    THE NIGERIAN INSTITUTE OF<br />QUANTITY SURVEYORS
                  </h1>
                  <p className="text-[9px] text-slate-600 mt-0.5">
                    No. 24, NIQS Crescent, Off Michael Ama Nnachi Crescent, Cadastral Zone B6, Mabushi District, Abuja
                  </p>
                  <p className="text-[9px] text-slate-600">
                    Postal Address: P. O. Box 10689, Garki, Abuja &nbsp;|&nbsp; Tel: 08028303346, 08167593807 &nbsp;|&nbsp; info@niqsng.org
                  </p>
                </div>
              </div>

              {/* Title */}
              <div className="text-center mb-5">
                <h2 className="text-base font-black tracking-widest uppercase underline">
                  {session?.year ?? ""} PROFESSIONAL EXAMINATIONS — TIMETABLE
                </h2>
                {session?.title && (
                  <p className="text-xs font-bold text-slate-600 mt-1 uppercase tracking-widest">{session.title}</p>
                )}
              </div>

              {/* Timetable */}
              <table className="w-full border-collapse border border-slate-900 text-[9px]">
                <thead>
                  <tr className="bg-slate-100">
                    <th className="border border-slate-900 px-2 py-1.5 font-black uppercase text-left w-28">Date</th>
                    <th className="border border-slate-900 px-2 py-1.5 font-black uppercase text-left w-24">Time</th>
                    {examColumns.map((col: any) => (
                      <th key={col.id} className="border border-slate-900 px-2 py-1.5 font-black uppercase text-center">
                        {col.title}<br />
                        <span className="font-black text-slate-700">({col.code})</span>
                      </th>
                    ))}
                    {examColumns.length === 0 && (
                      <th className="border border-slate-900 px-2 py-1.5 text-center text-slate-400">
                        No entries yet
                      </th>
                    )}
                  </tr>
                </thead>
                <tbody>
                  {sortedDates.map(date => {
                    const slots = rows[date] ?? {};
                    const slotKeys = Object.keys(slots);
                    const { day, date: dateLabel } = fmtDate(date);
                    return slotKeys.map((slotKey, slotIdx) => {
                      const slot = slots[slotKey];
                      return (
                        <tr key={`${date}-${slotKey}`}>
                          {slotIdx === 0 && (
                            <td className="border border-slate-900 px-2 py-1.5 font-black align-top leading-tight" rowSpan={slotKeys.length}>
                              <div>{day}</div>
                              <div>{dateLabel}</div>
                            </td>
                          )}
                          <td className="border border-slate-900 px-2 py-1.5 font-bold whitespace-nowrap align-middle">
                            {slot.start_time}–{slot.end_time}
                          </td>
                          {examColumns.map((col: any) => (
                            <td key={col.id} className="border border-slate-900 px-2 py-1.5 align-middle leading-snug">
                              {slot.subjects?.[col.id]?.subject ?? ""}
                            </td>
                          ))}
                        </tr>
                      );
                    });
                  })}
                  {sortedDates.length === 0 && (
                    <tr>
                      <td colSpan={2 + examColumns.length} className="border border-slate-900 px-2 py-6 text-center text-slate-400 font-bold">
                        Timetable not yet published
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>

              <div className="text-center mt-6">
                <p className="text-xs font-black uppercase tracking-widest border border-slate-900 inline-block px-4 py-1">
                  NB: ALWAYS BRING YOUR ADMISSION CARD TO THE EXAMINATION HALL
                </p>
              </div>
            </div>
          </div>
        )}
      </div>
    </>
  );
}
